/**
 * EventMarker Component
 *
 * Renders clickable event markers at phase boundaries.
 * Events are the primary interactive elements in the timeline - they represent
 * farmer actions (inoculation, transfer, harvest) rather than passive waiting periods.
 *
 * Visual Design:
 * - Different shapes for different event types (dot, diamond, star)
 * - Solid fill for actual events, hollow for planned events
 * - Positioned at event dates along the timeline
 * - Hover state indicates clickability
 * - Click opens EventCard with event details and lineage
 */

import React from 'react'
import { cn } from '@/lib/utils'

/**
 * Event type to visual mapping
 */
const EVENT_SHAPES = {
  spawn_inoculated: 'dot',
  substrate_inoculated: 'dot',
  transferred: 'diamond',          // Legacy
  induction: 'diamond',
  first_flush: 'star',
  second_flush: 'star',
  third_flush: 'star',
  fourth_flush: 'star',
  spent: 'dot',                    // End of lifecycle
  // Legacy event types for backwards compatibility
  inoculated: 'dot',
  colonization_complete: 'dot',
  moved_to_fruiting: 'dot',
  harvested: 'star',
  // Future events default to hollow dot
  planned: 'hollow-dot'
}

/**
 * Event type to color mapping
 * (Follows phase color scheme for visual consistency)
 */
const EVENT_COLORS = {
  spawn_inoculated: '#8b5cf6',      // purple (spawn_incubation phase)
  substrate_inoculated: '#f59e0b',  // amber (spawn_run phase)
  transferred: '#10b981',           // emerald (pinning phase) - legacy
  induction: '#10b981',             // emerald (pinning phase)
  first_flush: '#3b82f6',           // blue (interval phase)
  second_flush: '#3b82f6',          // blue
  third_flush: '#3b82f6',           // blue
  fourth_flush: '#3b82f6',          // blue
  spent: '#6b7280',                 // gray (end of lifecycle)
  // Legacy event types for backwards compatibility
  inoculated: '#8b5cf6',
  colonization_complete: '#8b5cf6',
  moved_to_fruiting: '#10b981',
  harvested: '#ef4444',
  planned: '#6b7280'
}

/**
 * Render SVG shape based on event type
 */
function EventShape({ type, isPast, size = 16 }) {
  const shape = EVENT_SHAPES[type] || 'dot'
  const color = EVENT_COLORS[type] || '#6b7280'
  const half = size / 2

  // For future/planned events, use hollow style
  const fill = isPast ? color : 'white'
  const stroke = color
  const strokeWidth = isPast ? 0 : 2

  switch (shape) {
    case 'dot':
      return (
        <circle
          cx={half}
          cy={half}
          r={half - 2}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
        />
      )

    case 'diamond':
      return (
        <polygon
          points={`${half},2 ${size - 2},${half} ${half},${size - 2} 2,${half}`}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
        />
      )

    case 'star': {
      // Simple 5-point star
      const points = []
      for (let i = 0; i < 10; i++) {
        const angle = (i * Math.PI) / 5 - Math.PI / 2
        const r = i % 2 === 0 ? half - 2 : (half - 2) * 0.5
        const x = half + r * Math.cos(angle)
        const y = half + r * Math.sin(angle)
        points.push(`${x},${y}`)
      }
      return (
        <polygon
          points={points.join(' ')}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
        />
      )
    }

    case 'hollow-dot':
    default:
      return (
        <circle
          cx={half}
          cy={half}
          r={half - 2}
          fill="white"
          stroke={color}
          strokeWidth={2}
        />
      )
  }
}

/**
 * EventMarker Component
 *
 * @param {Object} props
 * @param {Object} props.event - Event data
 * @param {string} props.event.type - Event type (e.g., 'inoculated', 'harvested')
 * @param {Date} props.event.date - Event date (actual_date or planned_date)
 * @param {string} props.event.status - 'completed' or 'planned'
 * @param {Object} props.position - Positioning data
 * @param {number} props.position.left - Horizontal position in pixels
 * @param {number} props.position.top - Vertical position in pixels (default: centered in row)
 * @param {Function} props.onClick - Click handler to open event card
 * @param {number} props.size - Marker size in pixels (default: 16)
 * @param {string} props.className - Additional CSS classes
 */
export default function EventMarker({
  event,
  position,
  onClick,
  size = 16,
  className
}) {
  const isPast = event.status === 'completed'
  const isPlanned = event.status === 'planned'

  // Format date for tooltip
  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  // Human-readable event type labels
  const eventTypeLabels = {
    spawn_inoculated: 'Spawn Inoculation',
    substrate_inoculated: 'Substrate Inoculation',
    transferred: 'Induction',        // Legacy
    induction: 'Induction',
    first_flush: 'First Flush',
    second_flush: 'Second Flush',
    third_flush: 'Third Flush',
    fourth_flush: 'Fourth Flush',
    spent: 'Spent',
    // Legacy event types for backwards compatibility
    inoculated: 'Inoculated',
    colonization_complete: 'Colonization Complete',
    moved_to_fruiting: 'Moved to Fruiting',
    harvested: 'Harvested',
    planned: 'Planned Event'
  }

  const label = eventTypeLabels[event.type] || event.type

  return (
    <div
      className={cn(
        'absolute z-10 cursor-pointer transition-transform hover:scale-125',
        'focus:outline-none focus-visible:outline-none',
        className
      )}
      style={{
        left: position.left - size / 2, // Center horizontally on event date
        top: position.top ? position.top - size / 2 : '50%', // Center vertically
        transform: position.top ? undefined : 'translateY(-50%)',
        width: size,
        height: size
      }}
      onClick={(e) => {
        e.stopPropagation()
        onClick?.(event, e)
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          e.stopPropagation()
          onClick?.(event, e)
        }
      }}
      role="button"
      tabIndex={0}
      title={`${label} - ${formatDate(event.date)}${isPlanned ? ' (Planned)' : ''}`}
      aria-label={`${label} event on ${formatDate(event.date)}`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="drop-shadow-sm"
      >
        <EventShape type={event.type} isPast={isPast} size={size} />
      </svg>

      {/* Visual indicator for planned events */}
      {isPlanned && (
        <div
          className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-blue-500 border border-white"
          aria-hidden="true"
        />
      )}
    </div>
  )
}

/**
 * Helper function to calculate event marker position
 * (Use this in parent component to calculate positions before rendering)
 *
 * @param {Date} eventDate - Event date
 * @param {Date} rangeStart - Timeline start date
 * @param {number} pixelsPerDay - Timeline scale
 * @returns {number} Horizontal position in pixels
 */
export function calculateEventPosition(eventDate, rangeStart, pixelsPerDay) {
  const daysBetween = (date1, date2) => {
    const oneDay = 24 * 60 * 60 * 1000
    return Math.round((date2 - date1) / oneDay)
  }

  return daysBetween(rangeStart, eventDate) * pixelsPerDay
}
